import { Candidate } from "@/types/candidate";
import { CandidateHeader } from "@/components/candidates/CandidateHeader";
import { ContactInformation } from "@/components/candidates/ContactInformation";
import { CandidateCV } from "@/components/candidates/CandidateCV";
import { CandidateSummary } from "@/components/candidates/CandidateSummary";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { FileSearch } from "lucide-react";
import { CandidateAnalysisModal } from "@/components/candidates/CandidateAnalysisModal";
import { useTranslation } from "react-i18next";

interface CandidateProfileProps {
  candidate: Candidate;
  cv: string | null;
  cvExists: boolean;
  onCVUpdate: (newCVUrl: string) => void;
}

export const CandidateProfile = ({
  candidate,
  cv,
  cvExists,
  onCVUpdate
}: CandidateProfileProps) => {
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const { t } = useTranslation();

  // Check if AI analysis exists and has at least one property
  const hasAiAnalysis = candidate.ai_analysis && Object.keys(candidate.ai_analysis).length > 0;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-1 w-full bg-white rounded-lg shadow p-6">
          <div className="mb-4">
            <CandidateHeader candidate={candidate} />
          </div>
          <ContactInformation candidate={candidate} />
        </div>
        <div className="w-full lg:max-w-xs bg-white rounded-lg shadow p-6">
          <CandidateCV 
            cv={cv} 
            cvExists={cvExists}
            candidateName={candidate.name} 
            candidateId={candidate.id}
            onCVUpdate={onCVUpdate}
          />
        </div>
      </div>

      {/* AI Synthesis Section - Full Width */}
      <div className="w-full bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold">{t("candidates.profile.aiSummary")}</h3>
          {hasAiAnalysis && (
            <Button 
              variant="outline" 
              size="sm" 
              className="flex items-center text-xs"
              onClick={() => setShowAnalysisModal(true)}
            >
              <FileSearch className="mr-1 h-4 w-4" />
              {t("candidates.profile.seeFullAnalysis")}
            </Button>
          )}
        </div>
        <CandidateSummary summary={candidate.ai_synthesis} />
      </div>

      {/* AI Analysis Modal */}
      {hasAiAnalysis && (
        <CandidateAnalysisModal 
          analysis={candidate.ai_analysis || {}}
          isOpen={showAnalysisModal}
          onClose={() => setShowAnalysisModal(false)}
          candidateName={candidate.name}
          candidateFunction={candidate.function}
          candidateSynthesis={candidate.ai_synthesis}
        />
      )}
    </div>
  );
};
